import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { Layout } from './Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { PaginationCustom } from './ui/pagination-custom';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import {
  Brain,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Copy,
  Download,
  Upload,
  Play,
  Home,
  RefreshCw,
  Eye,
  Settings,
  FileText,
  Code,
  MessageSquare,
  Target,
  ListChecks,
  BookOpen,
  Archive,
  CheckCircle,
  AlertTriangle,
  Clock,
  MoreHorizontal,
  GitBranch,
  Tag,
  X,
  Save,
  LayoutGrid,
  TestTube,
  Users
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { toast } from 'sonner';
import { AppContext } from '../src/App';
import { useAuth } from './AuthContext';
import { apiClient } from '@/Utils/apiClient';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/config';

// Mock data for AI Prompts
const mockPrompts = [
  {
    id: 1,
    name: 'Patient Risk Assessment',
    description: 'Evaluates patient risk factors for post-operative complications based on medical history, current medications, and clinical indicators. This comprehensive assessment helps healthcare providers make informed decisions about surgical procedures and post-operative care planning.',
    category: 'Healthcare',
    status: 'Active',
    version: '2.1',
    author: 'Dr. Sarah Mitchell',
    linkedDocumentType: 'Clinical Notes',
    outputFormat: 'JSON',
    lastUpdated: '2024-01-15',
    usageCount: 245,
    successRate: 94.2,
    avgResponseTime: 1.8,
    tags: ['risk-assessment', 'clinical', 'surgery'],
  },
  {
    id: 2,
    name: 'Medication Review Assistant',
    description: 'Reviews medication lists for potential drug interactions, contraindications, and optimization opportunities. Analyzes patient-specific factors including age, weight, kidney function, and existing conditions to provide comprehensive medication safety assessments.',
    category: 'Pharmacy',
    status: 'Testing',
    version: '1.0',
    author: 'Dr. Michael Chen',
    linkedDocumentType: 'Medication List',
    outputFormat: 'Structured Text',
    lastUpdated: '2024-01-12',
    usageCount: 89,
    successRate: 91.5,
    avgResponseTime: 2.1,
    tags: ['medication', 'pharmacy', 'interactions'],
  },
  {
    id: 3,
    name: 'Clinical Documentation Helper',
    description: 'Assists in generating comprehensive clinical documentation by analyzing patient encounter data, symptoms, diagnostic findings, and treatment plans. Ensures compliance with medical coding standards and regulatory requirements while maintaining clinical accuracy.',
    category: 'Documentation',
    status: 'Active',
    version: '3.0',
    author: 'Dr. Lisa Anderson',
    linkedDocumentType: 'Patient Records',
    outputFormat: 'Formatted Text',
    lastUpdated: '2024-01-10',
    usageCount: 356,
    successRate: 96.8,
    avgResponseTime: 1.5,
    tags: ['documentation', 'coding', 'compliance'],
  },
  {
    id: 4,
    name: 'Diagnostic Decision Support',
    description: 'Provides evidence-based diagnostic suggestions based on patient symptoms, laboratory results, imaging findings, and clinical history. Helps healthcare providers consider differential diagnoses and appropriate next steps in patient care.',
    category: 'Diagnostics',
    status: 'Draft',
    version: '1.2',
    author: 'Dr. James Wilson',
    linkedDocumentType: 'Lab Results',
    outputFormat: 'JSON',
    lastUpdated: '2024-01-08',
    usageCount: 12,
    successRate: 88.3,
    avgResponseTime: 2.3,
    tags: ['diagnostics', 'decision-support', 'evidence-based'],
  },
  {
    id: 5,
    name: 'Treatment Protocol Optimizer',
    description: 'Optimizes treatment protocols based on patient-specific factors, current evidence-based guidelines, and institutional best practices. Considers contraindications, drug allergies, and patient preferences to recommend personalized treatment approaches.',
    category: 'Treatment',
    status: 'Active',
    version: '2.3',
    author: 'Dr. Emma Thompson',
    linkedDocumentType: 'Treatment Plans',
    outputFormat: 'XML',
    lastUpdated: '2024-01-14',
    usageCount: 198,
    successRate: 93.7,
    avgResponseTime: 1.9,
    tags: ['treatment', 'protocols', 'optimization'],
  },
];

const statusColors = {
  'Active': 'bg-green-100 text-green-800 border-green-200',
  'ACTIVE': 'bg-green-100 text-green-800 border-green-200',

  'Testing': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Draft': 'bg-gray-100 text-gray-800 border-gray-200',
  'DRAFT': 'bg-gray-100 text-gray-800 border-gray-200',

  'Archived': 'bg-red-100 text-red-800 border-red-200',
};

// const categories = ['All', 'Healthcare', 'Pharmacy', 'Documentation', 'Diagnostics', 'Treatment'];
const statuses = ['Active', 'Test', 'Draft', 'Archived'];
interface HoverCardProps {
  children: React.ReactNode;
  content: React.ReactNode;
}

export function AIPromptManager() {
  const { user, token } = useAuth();
  const { sideBarItems, setCurrentView, selectedPromptData, setSelectedPromptData, setPagination, pagination } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(pagination.promptManager || 1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [promptToDelete, setPromptToDelete] = useState<any>(null);
  const [view, setView] = useState<'list' | 'cards'>(pagination.promptManagerView || 'list');
  const [prompts, setApiPrompts] = useState<any>([]);
  const [categories, setCategory] = useState<string[]>([]);
  const [models, setModels] = useState<Array<{ id: string; name: string }>>([]);

  const promptsMenu = sideBarItems.find((item: any) => item.name === "Prompts");

  const hasPermission = (permission: string) => {
    return promptsMenu?.userPermissions?.includes(permission);
  }

  // Filter prompts based on search and filters
  // const filteredPrompts = useMemo(() => {
  //   return prompts.filter((prompt: any) => {
  //     const matchesSearch =
  //       prompt.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       prompt.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       prompt.author?.toLowerCase().includes(searchTerm.toLowerCase());

  //     const matchesCategory =
  //       selectedCategory === 'All' || prompt.category === selectedCategory;

  //     const matchesStatus =
  //       selectedStatus === 'All' || prompt.status === selectedStatus;

  //     return matchesSearch && matchesCategory && matchesStatus;
  //   });
  // }, [prompts, searchTerm, selectedCategory, selectedStatus]);



  // const fetchData = async () => {
  //   try {
  //     const response = await fetch(`${import.meta.env.VITE_API_URL}/prompts`, {

  //       method: 'GET',
  //       cache: 'no-store',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `Bearer ${token}`
  //       },
  //     });

  //     const data = await response.json();

  //     if (!response.ok) {
  //       throw new Error(data.error || 'Request failed');
  //     }
  //     // const newData = await generateMockHarvestData(data)
  //     setApiPrompts(data?.data)
  //     return data;
  //   } catch (error: any) {
  //     console.error('Fetch error:', error.message);
  //     return null;
  //   }
  // };

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();

      if (searchTerm) params.append("search", searchTerm);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedStatus) params.append("status", selectedStatus);
      // Resolve the model object (by id or by name)
      const model = selectedModel ? (models.find((m) => m.id === selectedModel) ?? models.find((m) => m.name === selectedModel)) : undefined;
      const modelId = model?.id ?? selectedModel;
      if (modelId) params.append("model_type", modelId);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}prompts?${params.toString()}`,
        {
          method: "GET",
          // cache: "no-store",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Request failed");
      }

      setApiPrompts(data?.data);
      return data;
    } catch (error: any) {
      console.error("Fetch error:", error.message);
      return null;
    }
  };



  const fetchCategory = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}prompts/category`, {
        method: 'GET',
        // cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      const val = data?.data; // assume data.data is an array like ['Healthcare', 'Pharmacy', ...]
      setCategory(val); // add 'All' as a default option

      return data;
    } catch (error: any) {
      console.error("Category fetch failed:", error.message);
      return null;
    }
  };

  const fetchModels = async () => {
    try {
      const response = await apiClient('prompts/ai/models', {
        method: 'GET',
        token
      });
      const data = await response.json();
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch models');
      }
      setModels(data?.data || []);
      return data;
    } catch (error: any) {
      console.error("Models fetch failed:", error.message);
      toast.error('Failed to fetch AI models');
      return null;
    }
  };


  useEffect(() => {
    fetchData();
    fetchCategory(); // if this only needs to run once, move it to another useEffect
  }, [searchTerm, selectedCategory, selectedStatus,selectedModel]);

  useEffect(() => {
    fetchModels();
  }, []);


  // Paginate results
  const paginatedPrompts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return prompts.slice(startIndex, startIndex + itemsPerPage);
  }, [prompts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(prompts.length / itemsPerPage);



  const handleCreatePrompt = () => {
    if (hasPermission("prompts:create")) {
      setSelectedPromptData(null);
      setCurrentView('prompt-detail');
      // Use a URL-friendly id for new prompts
      navigate(ROUTES.promptCreate, {
        state: { prompt: null }
      });
    } else {
      toast.error("You don't have permission to create a prompt")
    }
  };

  const handleEditPrompt = (prompt: any) => {
    if (!hasPermission("prompts:edit")) {
      toast.error("You don't have permission to edit a prompt")
      return
    }
    setSelectedPromptData({ ...prompt, mode: 'edit' });
    setCurrentView('prompt-detail');
    // Navigate with prompt id so the detail screen can be deep-linked
    const promptId = prompt?.prompt_id ?? prompt?.id;
    const promptData = { ...selectedPromptData, mode: 'edit' }
    if (promptId) {
      navigate(ROUTES.promptDetail,
        { state: { prompt: promptData } });
    }
  };

  const handleViewPrompt = (prompt: any) => {
    setSelectedPromptData({ ...prompt, mode: 'view' });
    setCurrentView('prompt-detail');
    const promptId = prompt?.prompt_id ?? prompt?.id;
    const promptData = { ...selectedPromptData, mode: 'view' }
    if (promptId) {
      navigate(ROUTES.promptDetail, {
        state: { prompt: promptData }
      });
    }
  };

  const activatePromts = async (promts: any) => {

    try {
      const response = await fetch(` ${import.meta.env.VITE_API_URL}/prompts/${promts?.prompt_id}/versions/${promts?.version_id}/activate`, {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        // body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      await fetchData()
      toast.success(data?.message || "Prompt activated successfully");
      // const newData = await generateMockHarvestData(data)
      return data;
    } catch (error: any) {

      return null;
    }
  }

  const handleDuplicatePrompt = async (prompt: any) => {
    if (!hasPermission("prompts:create")) {
      toast.error("You don't have permission to duplicate a prompt")
      return
    }
    try {
      const payload = {
        name: prompt?.name,
      }
      const response = await apiClient(`prompts/duplicate/${prompt?.version_id}`, {
        method: "POST",
        token,
        body: payload

      })
      const result = await response.json()
      if (!result?.success) {
        throw new Error(result?.error)
      }
      else {
        toast.success(result?.message || 'Prompt duplicated successfully')
      }

    }
    catch (error: any) {
      if (error.status < 500)
        toast.error(error?.message || 'Failed to duplicate prompt')
      else
        toast.error('Something went wrong')

    }
    finally {
      fetchData()

    }
    // const duplicatedPrompt = {
    //   ...prompt,
    //   id: Date.now(),
    //   name: `${prompt.name} (Copy)`,
    //   status: 'Draft',
    //   version: '1.0',
    //   usageCount: 0,
    //   mode: 'create'
    // };
    // setSelectedPromptData(duplicatedPrompt);
    // setCurrentView('prompt-detail');
    // toast.success('Prompt duplicated successfully');
  };

  const handleDeletePrompt = async (versionId: any) => {

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}prompts/versions/${versionId}`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      })
      const data = await response?.json();
      if (!response.ok) {
        toast.error(data?.message || data?.error || "Request failed");
        return;
      }
      await fetchData()
      toast.success(data?.message || "Prompt archived successfully");
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setPromptToDelete(null);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');          // Clear search input
    setSelectedCategory('');    // Reset category filter
    setSelectedModel('');       // Reset model filter
    setSelectedStatus('');      // Reset status filter
  };


  const handleTestPrompt = (prompt: any) => {
    toast.success(`Testing prompt: ${prompt.name}`);
  };
  const onPageChanged = (page: number) => {
    setCurrentPage(page);
    setPagination(prev => ({
      ...prev,
      promptManager: page
    }))
  }

  const HoverCard: React.FC<HoverCardProps> = ({ children, content }) => {
    const [open, setOpen] = useState(false);
    const [align, setAlign] = useState<"center" | "top" | "bottom">("center");
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (open && ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // if not enough space below, align from bottom
        if (spaceBelow < 150) {
          setAlign("bottom");
        }
        // if not enough space above, align from top
        else if (spaceAbove < 150) {
          setAlign("top");
        }
        else {
          setAlign("center");
        }
      }
    }, [open]);

    return (
      <div
        ref={ref}
        className="relative inline-block"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
        {open && (
          <div
            className={`absolute right-full mr-2 z-50 w-64 rounded-2xl bg-white p-3 shadow-xl border
            ${align === "center"
                ? "top-1/2 -translate-y-1/2"
                : align === "top"
                  ? "top-0"
                  : "bottom-0"
              }`}
          >
            {content}
          </div>
        )}
      </div>
    );
  };
  const renderListView = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[16px]">
          <Brain className="w-5 h-5 text-blue-600" />
          AI Prompts ({prompts.length})
        </CardTitle>
        <CardDescription className='text-[14px]' >
          Manage and configure AI prompts for healthcare audit workflows
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Category</TableHead>
                <TableHead className="w-[200px] font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Name</TableHead>
                <TableHead className="w-[300px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Description</TableHead>
                <TableHead className="w-[100px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Status</TableHead>
                <TableHead className="w-[100px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Version</TableHead>
                <TableHead className="w-[150px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Author</TableHead>
                <TableHead className="w-[100px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Usage</TableHead>
                <TableHead className="w-[120px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Success Rate</TableHead>
                <TableHead className="w-[150px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Model</TableHead>
                <TableHead className="w-[100px]font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prompts?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="w-8 h-8 text-gray-400" />
                      <p className="text-gray-500" style={{ fontSize: '11.2px' }}>
                        No prompts found matching your filters
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetFilters}
                        className="font-inter font-semibold text-[14px] leading-[20px] tracking-normal text-[#2C3E5D]"
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPrompts?.map((prompt: any, index: number) => (
                  <TableRow
                    key={prompt.id ?? index}
                    className="hover:bg-gray-50/50 cursor-pointer transition-colors"
                    onClick={() => handleViewPrompt(prompt)}
                  >
                    <TableCell className='w-[120px]'>
                      <Badge variant="outline" className="text-sm">
                        {prompt.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium text-gray-900">{prompt.name}</div>
                        <div className="flex items-center gap-1 flex-wrap">
                          {prompt?.tags?.map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-0">
                      <div className="text-sm text-gray-600 truncate" title={prompt.description}>
                        {prompt.description}
                      </div>
                    </TableCell>

                    <TableCell>
                      <Badge className={`${statusColors[prompt.status as keyof typeof statusColors]} text-sm border`}>
                        {String(prompt.status)?.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">v{prompt.version}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600 truncate" title={prompt.author}>
                        {prompt?.author}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-900" >{prompt?.usage_count}</div>
                    </TableCell>
                    {/* <TableCell>
                        <div className="text-sm text-gray-900" onMouseOver={()=>handleShowDetail(prompt)} >{prompt?.success_rate || 0}%
                      </div>
                    </TableCell> */}
                    <TableCell>
                      <HoverCard
                        content={
                          <div className="text-sm space-y-1">
                            <p className="font-semibold">SuccessRate Details</p>
                            <p>Total Chart Reviewed: {prompt?.total_charts_reviewed ?? 0}</p>
                            <p>Agreed With AI: {prompt?.agreed_with_ai ?? 0}</p>
                            <p>Disagreed With AI: {prompt?.disagreed_with_ai ?? 0}</p>
                          </div>
                        }
                      >
                        <div className="text-sm text-gray-900 cursor-pointer">
                          {prompt?.success_rate ?? 0}%
                        </div>
                      </HoverCard>
                    </TableCell>
                    <TableCell className="max-w-[150px] align-top">
                      <div className="text-sm text-gray-600 break-words whitespace-normal">
                        {prompt?.model_type ?? '-'}
                      </div>
                    </TableCell>
                    <TableCell className="align-bottom">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => handleViewPrompt(prompt)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {hasPermission("prompts:edit") && (
                            <DropdownMenuItem onClick={() => handleEditPrompt(prompt)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Prompt
                            </DropdownMenuItem>)}
                          {/* <DropdownMenuItem onClick={() => handleTestPrompt(prompt)}>
                            <TestTube className="w-4 h-4 mr-2" />
                            Test Prompt
                          </DropdownMenuItem> */}
                          {hasPermission("prompts:create") && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(), handleDuplicatePrompt(prompt) }}>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>)}
                          {hasPermission("prompts:edit") && prompt.status !== 'ACTIVE' && (
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation(); activatePromts(prompt)
                            }}>
                              <Code className="w-4 h-4 mr-2" />
                              Activate
                            </DropdownMenuItem>)}
                          {hasPermission("prompts:archive") && (<>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={prompt.status == 'ARCHIVED'}
                              onClick={(e) => {
                                e.stopPropagation(); setPromptToDelete(prompt)
                              }}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Archive className="w-4 h-4 mr-2" />
                              Archive
                            </DropdownMenuItem></>)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <PaginationCustom
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={prompts.length}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChanged}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                // setCurrentPage(1);
                onPageChanged(1);
              }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderCardsView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {paginatedPrompts?.map((prompt) => (
        <Card
          key={prompt.id}
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => handleViewPrompt(prompt)}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="space-y-1 flex-1 min-w-0">
                <CardTitle className="text-base truncate" title={prompt.name}>
                  {prompt.name}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className={`${statusColors[prompt.status as keyof typeof statusColors]} text-xs border`}>
                    {prompt.status}
                  </Badge>
                  <span className="text-xs text-gray-500">v{prompt.version}</span>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {/* <DropdownMenuItem onClick={() => handleViewPrompt(prompt)}>
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  {hasPermission("prompts:edit") && (
                  <DropdownMenuItem onClick={() => handleEditPrompt(prompt)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Prompt
                  </DropdownMenuItem>)} */}
                  {/* <DropdownMenuItem onClick={() => handleTestPrompt(prompt)}>
                    <TestTube className="w-4 h-4 mr-2" />
                    Test Prompt
                  </DropdownMenuItem> */}
                  {hasPermission("prompts:create") && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicatePrompt(prompt) }}>
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>)}
                  {hasPermission("prompts:edit") && prompt.status !== 'ACTIVE' && (
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation(); activatePromts(prompt)
                    }}>
                      <Code className="w-4 h-4 mr-2" />
                      Activate
                    </DropdownMenuItem>)}
                  {hasPermission("prompts:archive") && (<>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        if (promptsMenu.userPermissions.includes("prompts:archive")) {
                          e.stopPropagation(); setPromptToDelete(prompt)
                        } else {
                          toast.error("You don't have permission to archive a prompt");
                        }
                      }}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Archive
                    </DropdownMenuItem></>)}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-4">
              <p className="text-sm text-gray-600 line-clamp-3" title={prompt?.description}>
                {prompt.description}
              </p>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Category:</span>
                  <Badge variant="outline" className="text-xs">
                    {prompt.category}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Usage:</span>
                  <span className="font-medium">{prompt?.usage_count}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Success Rate:</span>
                  {/* <span className="font-medium">{prompt?.success_rate}%</span> */}
                  <HoverCard
                    content={
                      <div className="text-sm space-y-1">
                        <p className="font-semibold">SuccessRate Details</p>
                        <p>Total Chart Reviewed: {prompt?.total_charts_reviewed ?? 0}</p>
                        <p>Agreed With AI: {prompt?.agreed_with_ai ?? 0}</p>
                        <p>Disagreed With AI: {prompt?.disagreed_with_ai ?? 0}</p>
                      </div>
                    }
                  >
                    <div className="text-sm text-gray-900 cursor-pointer">
                      {prompt?.success_rate ?? 0}%
                    </div>
                  </HoverCard>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Author:</span>
                  <span className="font-medium truncate ml-2" title={prompt?.author}>
                    {prompt?.author}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                {prompt?.tags?.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewPrompt(prompt);
                  }}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                {hasPermission("prompts:edit") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPrompt(prompt);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>)}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {totalPages > 1 && (
        <div className="col-span-full flex justify-center mt-6">
          <PaginationCustom
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={prompts.length}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChanged}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              // setCurrentPage(1);
              onPageChanged(1)
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="p-8  mx-auto">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <Breadcrumb>
            <BreadcrumbList>
              {/* <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => setCurrentView('dashboard')}
                  className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
                >
                  <Home className="w-4 h-4" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator /> */}
              <BreadcrumbItem>
                <BreadcrumbPage className="flex items-center gap-2 ">
                  <Brain className="w-4 h-4" />
                  AI Prompt Manager
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-inter font-semibold text-2xl leading-[100%] tracking-[0]  mb-2">
                AI Prompt Manager
              </h1>
              <p className="font-inter font-normal text-[16px] leading-[20px] tracking-normal">
                Create, manage, and optimize AI prompts for healthcare audit workflows
              </p>
            </div>
            {hasPermission("prompts:create") && (
              <div className="flex items-center gap-2">
                <Button onClick={handleCreatePrompt}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Prompt
                </Button>
              </div>)}
          </div>
        </div>

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
              <div className="flex-1 max-w-sm"> */}
            <div className="flex flex-wrap lg:flex-nowrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search prompts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger className="w-auto min-w-[120px] max-w-[200px] h-auto min-h-9 whitespace-normal break-words [&_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-value]]:text-left">
                    <SelectValue placeholder="Model" />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex items-center border rounded-md">
                  <Button
                    variant={view === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => { setView('list'), setPagination(prev => ({ ...prev, promptManagerView: 'list' })) }}
                    className="rounded-r-none"
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={view === 'cards' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => { setView('cards'), setPagination(prev => ({ ...prev, promptManagerView: 'cards' })) }}
                    className="rounded-l-none"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={resetFilters}
                  className="font-inter font-semibold text-[14px] leading-[20px] tracking-normal text-[#2C3E5D]"
                >
                  <RefreshCw className="w-4 h-4 mr-2 text-[#2C3E5D]" />
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        {view === 'list' ? renderListView() : renderCardsView()}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!promptToDelete} onOpenChange={() => setPromptToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Prompt</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to Archive "{promptToDelete?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDeletePrompt(promptToDelete?.version_id)}
                className="bg-red-600 hover:bg-red-700"
              >
                Archive
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}